/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { ErrorBoundary } from "react-error-boundary";
import { useLocation } from "react-router-dom";

import { ROUTES } from "Router";
import { checkRouteValidity } from "Router/RoutesUtils";
import openToast from "utils/openToast";
import warning from "utils/warning";

import { useAuth, usePrimaryEvents, useRole } from "state";

import LoadingScreen from "components/ui/LoadingScreen/LoadingScreen";
import { ErrorPage, ServerError, UnAuthorized } from "pages";
import * as Sentry from "@sentry/react";
import { sentryCaptureError } from "utils/errorFormatter";

const RouteManager = ({ children }) => {
  const { isLoggedIn, isLoading: isAuthLoading } = useAuth();
  const { pathname } = useLocation();
  const { isLoading: isRoleLoading } = useRole();
  const { isLoading: isFetchingEvents, isSwitchingEvent } = usePrimaryEvents();
  if (isAuthLoading || isRoleLoading || isFetchingEvents || isSwitchingEvent) {
    return <LoadingScreen test-id="DhU7QMLN" />;
  }

  const { routeHasEventId, routeShouldHaveEventId } = checkRouteValidity();
  if (
    // if Event id exists in a route that doesn't need it
    (isLoggedIn && routeHasEventId && !routeShouldHaveEventId) ||
    // OR the user hasn't selected an event
    (isLoggedIn && pathname === "/" && !routeHasEventId)
  ) {
    // Redirect to base (workspaces)
    window.location.replace(ROUTES.BASE);
    return <LoadingScreen />;
  }

  //  if logged in, show the route
  return <SafeComponent test-id="Cm52ZWJb">{children}</SafeComponent>;
};
// the alerts is used to prevent the error boundary from executing multiple times
let alerts = 0;

export const SafeComponent = ({ children }) => {
  const { pathname } = useLocation();

  return (
    <ErrorBoundary
      fallbackRender={({ error }) => {
        Sentry.captureException(error);

        if (error.status === 401) {
          return <UnAuthorized />;
        }

        if (error.status && error.status < 500 && error.status >= 400) {
          return <ErrorPage />;
        }

        return <ServerError />;
      }}
      onError={(error, info) => {
        if (alerts > 0) return;
        sentryCaptureError(error, "React Error Boundary");

        warning(error, info);

        openToast(
          "An error happened. Please report the incident to the support.",
          "error",
        );

        return (alerts += 1);
      }}
      resetKeys={[pathname]}
      onResetKeysChange={() => {
        alerts = 0;
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

export default RouteManager;
